<template>
  <div class="bg-white shadow-md rounded sm:rounded-lg p-4 sm:p-6">
    <property-details :accountNumber="property.Account_Num" :property="property" :isRegistrationClosedOrFullyOpened="isRegistrationClosedOrFullyOpened"></property-details>

    <div class="border-t border-gray-200 mt-6 pt-5">
      <div class="space-y-2 mb-5">
        <h4 class="text-xl font-semibold text-gray-900">Property Additional Information</h4>
        <p class="text-sm font-medium text-gray-600">These very specific questions will help us negotiate a lower
          value
          by comparing your house to properties that have recently sold.</p>
        <p class="text-sm font-medium text-gray-600">Please answer the following questions honestly even if your
          answer
          appears to support a higher value.</p>
        <p class="text-sm font-medium text-gray-600">
          <a href="https://youtu.be/RZAuxQaMKEE" target="_blank" class="text-sm font-medium text-indigo-600 underline break-all">Watch this video for a full
            explanation</a>
        </p>
      </div>
      <!-- # Bedrooms -->
      <fieldset>
        <div>
          <label class="block text-sm font-medium text-gray-900 mb-2">
            # Bedrooms
          </label>

          <div>
            <select v-model="data.total_bedrooms" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.total_bedrooms }">
              <option value="" selected disabled>Please Select</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6+">6+</option>
            </select>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.total_bedrooms">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.total_bedrooms }}
        </div>
      </fieldset>

      <!-- # Bathrooms -->
      <fieldset>
        <div class="mt-7">
          <label class="block text-sm font-medium text-gray-900 mb-2">
            # Bathrooms
          </label>

          <div>
            <select v-model="data.total_bathrooms" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.total_bathrooms }">
              <option value="" selected disabled>Please Select</option>
              <option value="1">1</option>
              <option value="1.5">1.5</option>
              <option value="2">2</option>
              <option value="2.5">2.5</option>
              <option value="3">3</option>
              <option value="3.5">3.5</option>
              <option value="4+">4+</option>
            </select>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.total_bathrooms">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.total_bathrooms }}
        </div>
      </fieldset>

      <!-- # Garage -->
      <fieldset>
        <div class="mt-7">
          <label class="block text-sm font-medium text-gray-900 mb-2">
            # Garage (carports don’t count)
          </label>

          <div>
            <select v-model="data.total_garage" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.total_garage }">
              <option value="" selected disabled>Please Select</option>
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4+">4+</option>
            </select>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.total_garage">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.total_garage }}
        </div>
      </fieldset>

      <fieldset>
        <div class="mt-7">
          <label class="block text-sm font-medium text-gray-900 mb-3">
            Check all that apply:
          </label>

          <div class="space-y-4">
            <div class="flex items-center">
              <input id="in_ground_swimming_pool" type="checkbox" v-model="data.in_ground_swimming_pool" value="1" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="in_ground_swimming_pool" class="ml-3 block text-sm font-medium text-gray-700">
                In-ground Swimming pool
              </label>
            </div>

            <div class="flex items-center">
              <input id="on_a_cul_de_sac" type="checkbox" v-model="data.on_a_cul_de_sac" value="1" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="on_a_cul_de_sac" class="ml-3 block text-sm font-medium text-gray-700">
                On a Cul-de-sac
              </label>
            </div>

            <div class="flex items-center">
              <input id="designated_historical_home" type="checkbox" v-model="data.designated_historical_home" value="1" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="designated_historical_home" class="ml-3 block text-sm font-medium text-gray-700">
                Designated historical home
              </label>
            </div>
          </div>
        </div>
      </fieldset>

      <h4 class="font-medium text-gray-900 mb-4 mt-7">Does your property have any of the following interior features?
      </h4>

      <!--Kitchen Counters-->
      <fieldset>
        <div>
          <label class="block text-sm font-medium text-gray-900 mb-2">
            Kitchen Counters
          </label>

          <div>
            <select v-model="data.kitchen_counter" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.kitchen_counter }">
              <option value="" selected disabled>Please Select</option>
              <option value="Formica (plastic type)">Formica (plastic type)</option>
              <option value="Granite or other natural stone">Granite or other natural stone</option>
              <option value="Corian/Quartz/Other solid surface/non-natural">Corian/Quartz/Other solid
                surface/non-natural</option>
              <option value="Concrete/Wood/Other">Concrete/Wood/Other</option>
              <option value="None/Other">None/Other</option>
            </select>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.kitchen_counter">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.kitchen_counter }}
        </div>
      </fieldset>

      <!--Flooring-->
      <fieldset>
        <div class="mt-7">
          <label class="block text-sm font-medium text-gray-900 mb-3">
            Flooring
            <span class="text-gray-400"> (Check all that apply) </span>
          </label>

          <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" :class="{ 'border border-red-500 p-1 rounded': errors.flooring }">
            <div class="flex items-center">
              <input id="genuine_wood" type="checkbox" v-model="data.flooring" value="Genuine wood" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="genuine_wood" class="ml-3 block text-sm font-medium text-gray-700">
                Genuine wood
              </label>
            </div>
            <div class="flex items-center">
              <input id="wood_alternative" type="checkbox" v-model="data.flooring" value="Wood alternative" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="wood_alternative" class="ml-3 block text-sm font-medium text-gray-700">
                Wood alternative
              </label>
            </div>
            <div class="flex items-center">
              <input id="marble" type="checkbox" v-model="data.flooring" value="Marble" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="marble" class="ml-3 block text-sm font-medium text-gray-700">
                Marble
              </label>
            </div>
            <div class="flex items-center">
              <input id="slate" type="checkbox" v-model="data.flooring" value="Slate" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="slate" class="ml-3 block text-sm font-medium text-gray-700">
                Slate
              </label>
            </div>
            <div class="flex items-center">
              <input id="travertine" type="checkbox" v-model="data.flooring" value="Travertine" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="travertine" class="ml-3 block text-sm font-medium text-gray-700">
                Travertine
              </label>
            </div>
            <div class="flex items-center">
              <input id="tile" type="checkbox" v-model="data.flooring" value="Tile (ceramic or porcelain)" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="tile" class="ml-3 block text-sm font-medium text-gray-700">
                Tile (ceramic or porcelain)
              </label>
            </div>
            <div class="flex items-center">
              <input id="vinyl" type="checkbox" v-model="data.flooring" value="Vinyl" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="vinyl" class="ml-3 block text-sm font-medium text-gray-700">
                Vinyl
              </label>
            </div>
            <div class="flex items-center">
              <input id="carpet" type="checkbox" v-model="data.flooring" value="Carpet" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="carpet" class="ml-3 block text-sm font-medium text-gray-700">
                Carpet
              </label>
            </div>
            <div class="flex items-center">
              <input id="None_Other" type="checkbox" v-model="data.flooring" value="None/Other" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="None_Other" class="ml-3 block text-sm font-medium text-gray-700">
                None/Other
              </label>
            </div>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.flooring">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.flooring }}
        </div>
      </fieldset>

      <!--Exterior Features-->
      <fieldset>
        <div class="mt-7">
          <label class="block font-medium text-gray-900 mb-4">
            Does your property have any of the following exterior features?
          </label>

          <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" :class="{ 'border border-red-500 p-1 rounded': errors.exterior_features }">
            <div class="flex items-center">
              <input id="storage_building" type="checkbox" v-model="data.exterior_features" value="Storage Building" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="storage_building" class="ml-3 block text-sm font-medium text-gray-700">
                Storage Building
              </label>
            </div>

            <div class="flex items-center">
              <input id="workshop" type="checkbox" v-model="data.exterior_features" value="Workshop" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="workshop" class="ml-3 block text-sm font-medium text-gray-700">
                Workshop
              </label>
            </div>

            <div class="flex items-center">
              <input id="workshop_w/Electric" type="checkbox" v-model="data.exterior_features" value="Workshop w/Electric" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="workshop_w/Electric" class="ml-3 block text-sm font-medium text-gray-700">
                Workshop w/Electric
              </label>
            </div>

            <div class="flex items-center">
              <input id="guest_house" type="checkbox" v-model="data.exterior_features" value="Guest House" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="guest_house" class="ml-3 block text-sm font-medium text-gray-700">
                Guest House
              </label>
            </div>

            <div class="flex items-center">
              <input id="garage_house" type="checkbox" v-model="data.exterior_features" value="Garage House" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="garage_house" class="ml-3 block text-sm font-medium text-gray-700">
                Garage House
              </label>
            </div>

            <div class="flex items-center">
              <input id="pool_house" type="checkbox" v-model="data.exterior_features" value="Pool House" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="pool_house" class="ml-3 block text-sm font-medium text-gray-700">
                Pool House
              </label>
            </div>

            <div class="flex items-center">
              <input id="outdoor_living_area" type="checkbox" v-model="data.exterior_features" value="Outdoor Living Area" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="outdoor_living_area" class="ml-3 block text-sm font-medium text-gray-700">
                Outdoor Living Area
              </label>
            </div>

            <div class="flex items-center">
              <input id="private_hanger" type="checkbox" v-model="data.exterior_features" value="Private Hanger" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="private_hanger" class="ml-3 block text-sm font-medium text-gray-700">
                Private Hanger
              </label>
            </div>

            <div class="flex items-center">
              <input id="private_landing_strip" type="checkbox" v-model="data.exterior_features" value="Private Landing Strip" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="private_landing_strip" class="ml-3 block text-sm font-medium text-gray-700">
                Private Landing Strip
              </label>
            </div>

            <div class="flex items-center">
              <input id="tennis_courts" type="checkbox" v-model="data.exterior_features" value="Tennis Court(s)" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="tennis_courts" class="ml-3 block text-sm font-medium text-gray-700">
                Tennis Court(s)
              </label>
            </div>

            <div class="flex items-center">
              <input id="sport_court" type="checkbox" v-model="data.exterior_features" value="Sport Court" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="sport_court" class="ml-3 block text-sm font-medium text-gray-700">
                Sport Court
              </label>
            </div>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.exterior_features">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.exterior_features }}
        </div>
      </fieldset>

      <!-- Features increasing property value -->
      <fieldset>
        <div class="mt-7">
          <label class="block font-medium text-gray-900 mb-4">
            Is your property located next to any of these items that may <u>increase</u> your property value?
          </label>

          <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" :class="{ 'border border-red-500 p-1 rounded': errors.features_increasing_property_value }">
            <div class="flex items-center">
              <input id="greenbelt" type="checkbox" v-model="data.features_increasing_property_value" value="Greenbelt" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="greenbelt" class="ml-3 block text-sm font-medium text-gray-700">
                Greenbelt
              </label>
            </div>

            <div class="flex items-center">
              <input id="airstrip" type="checkbox" v-model="data.features_increasing_property_value" value="Airstrip" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="airstrip" class="ml-3 block text-sm font-medium text-gray-700">
                Airstrip
              </label>
            </div>

            <div class="flex items-center">
              <input id="creek" type="checkbox" v-model="data.features_increasing_property_value" value="Creek" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="creek" class="ml-3 block text-sm font-medium text-gray-700">
                Creek
              </label>
            </div>

            <div class="flex items-center">
              <input id="golf_course_lot" type="checkbox" v-model="data.features_increasing_property_value" value="Golf Course Lot" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="golf_course_lot" class="ml-3 block text-sm font-medium text-gray-700">
                Golf Course Lot
              </label>
            </div>

            <div class="flex items-center">
              <input id="pond_tank" type="checkbox" v-model="data.features_increasing_property_value" value="Pond/Tank" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="pond_tank" class="ml-3 block text-sm font-medium text-gray-700">
                Pond/Tank
              </label>
            </div>

            <div class="flex items-center">
              <input id="water_lake_view" type="checkbox" v-model="data.features_increasing_property_value" value="Water/Lake View" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="water_lake_view" class="ml-3 block text-sm font-medium text-gray-700">
                Water/Lake View
              </label>
            </div>

            <div class="flex items-center">
              <input id="waterfront" type="checkbox" v-model="data.features_increasing_property_value" value="Waterfront" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="waterfront" class="ml-3 block text-sm font-medium text-gray-700">
                Waterfront
              </label>
            </div>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.features_increasing_property_value">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.features_increasing_property_value }}
        </div>
      </fieldset>

      <!-- Features decreasing property value -->
      <fieldset>
        <div class="mt-7">
          <label class="block font-medium text-gray-900 mb-4">
            Is your property located next to any of these items that may <u>decrease</u> your property value?
          </label>

          <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" :class="{ 'border border-red-500 p-1 rounded': errors.features_decreasing_property_value }">
            <div class="flex items-center">
              <input id="very_busy_road" type="checkbox" v-model="data.features_decreasing_property_value" value="Very busy road" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="very_busy_road" class="ml-3 block text-sm font-medium text-gray-700">
                Very busy road
              </label>
            </div>

            <div class="flex items-center">
              <input id="very_large_overhead_power_lines" type="checkbox" v-model="data.features_decreasing_property_value" value="Very large overhead power lines" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="very_large_overhead_power_lines" class="ml-3 block text-sm font-medium text-gray-700">
                Very large overhead power lines
              </label>
            </div>

            <div class="flex items-center">
              <input id="large_radio_cellphone_tower" type="checkbox" v-model="data.features_decreasing_property_value" value="Large radio/cellphone tower" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="large_radio_cellphone_tower" class="ml-3 block text-sm font-medium text-gray-700">
                Large radio/cellphone tower
              </label>
            </div>

            <div class="flex items-center">
              <input id="commercial_property" type="checkbox" v-model="data.features_decreasing_property_value" value="Commercial property" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="commercial_property" class="ml-3 block text-sm font-medium text-gray-700">
                Commercial property
              </label>
            </div>

            <div class="flex items-center">
              <input id="junky_neighbors" type="checkbox" v-model="data.features_decreasing_property_value" value="Junky neighbors" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="junky_neighbors" class="ml-3 block text-sm font-medium text-gray-700">
                Junky neighbors
              </label>
            </div>

            <div class="flex items-center">
              <input id="drainage_flooding_area" type="checkbox" v-model="data.features_decreasing_property_value" value="Drainage/flooding area" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="drainage_flooding_area" class="ml-3 block text-sm font-medium text-gray-700">
                Drainage/flooding area
              </label>
            </div>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.features_decreasing_property_value">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.features_decreasing_property_value }}
        </div>
      </fieldset>

      <!-- Remodeled -->
      <fieldset>
        <div class="mt-7">
          <label class="block text-sm font-medium text-gray-900 mb-2">
            Have you significantly remodeled or updated your house since you bought it?
          </label>

          <div>
            <select v-model="data.remodeled" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.remodeled }">
              <option value="" selected disabled>Please Select</option>
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.remodeled">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.remodeled }}
        </div>
      </fieldset>

      <!-- Remodeling year -->
      <fieldset v-if="data.remodeled == 'Yes'">
        <div class="mt-7">
          <label class="block text-sm font-medium text-gray-900 mb-2">
            What is the most recent year you remodeled?
          </label>

          <div class="space-y-4">
            <input type="number" v-model="data.remodeling_year" placeholder="Please enter year" min=1900 :max="current_year" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.remodeling_year }" />
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.remodeling_year">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.remodeling_year }}
        </div>
      </fieldset>

      <!-- Remodeling prompted -->
      <fieldset v-if="data.remodeled == 'Yes'">
        <div class="mt-7">
          <label class="block text-sm font-medium text-gray-900 mb-2">
            Was your remodel prompted by an event that damaged the property such as a fire, flood, destructive
            tenant or other event?
          </label>

          <div>
            <select v-model="data.remodeling_prompted" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.remodeling_prompted }">
              <option value="" selected disabled>Please Select</option>
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.remodeling_prompted">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.remodeling_prompted }}
        </div>

        <div class="mt-6 space-y-3" v-if="data.remodeling_prompted == 'Yes'">
          <select v-model="data.remodeling_prompted_by_event" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.remodeling_prompted_by_event }">
            <option value="" selected disabled>Please Select</option>
            <option value="Fire">Fire</option>
            <option value="Flood">Flood</option>
            <option value="Destructive tenant">Destructive tenant</option>
            <option value="Other event">Other event</option>
          </select>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.remodeling_prompted_by_event">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.remodeling_prompted_by_event }}
        </div>
      </fieldset>

      <!--Amenities remodeled-->
      <fieldset v-if="data.remodeled == 'Yes'">
        <div class="mt-7">
          <label class="block font-semibold text-gray-900 mb-4">
            What did you remodel?
          </label>

          <div class="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4" :class="{ 'border border-red-500 p-1 rounded': errors.amenities_remodeled }">
            <div class="flex items-center">
              <input id="flooring" type="checkbox" v-model="data.amenities_remodeled" value="Flooring" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="flooring" class="ml-3 block text-sm font-medium text-gray-700">
                Flooring
              </label>
            </div>

            <div class="flex items-center">
              <input id="Paint" type="checkbox" v-model="data.amenities_remodeled" value="Paint" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="Paint" class="ml-3 block text-sm font-medium text-gray-700">
                Paint
              </label>
            </div>

            <div class="flex items-center">
              <input id="kitchen_countertops" type="checkbox" v-model="data.amenities_remodeled" value="Kitchen countertops" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="kitchen_countertops" class="ml-3 block text-sm font-medium text-gray-700">
                Kitchen countertops
              </label>
            </div>

            <div class="flex items-center">
              <input id="converted_garage_to_living_space" type="checkbox" v-model="data.amenities_remodeled" value="Converted garage to living space" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="converted_garage_to_living_space" class="ml-3 block text-sm font-medium text-gray-700">
                Converted garage to living space
              </label>
            </div>

            <div class="flex items-center">
              <input id="added_on_a_room" type="checkbox" v-model="data.amenities_remodeled" value="Added on a room" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="added_on_a_room" class="ml-3 block text-sm font-medium text-gray-700">
                Added on a room
              </label>
            </div>

            <div class="flex items-center">
              <input id="electrical_system" type="checkbox" v-model="data.amenities_remodeled" value="Electrical system" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="electrical_system" class="ml-3 block text-sm font-medium text-gray-700">
                Electrical system
              </label>
            </div>

            <div class="flex items-center">
              <input id="plumbing_system" type="checkbox" v-model="data.amenities_remodeled" value="Plumbing system" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="plumbing_system" class="ml-3 block text-sm font-medium text-gray-700">
                Plumbing system
              </label>
            </div>

            <div class="flex items-center">
              <input id="foundation_work" type="checkbox" v-model="data.amenities_remodeled" value="Foundation work" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="foundation_work" class="ml-3 block text-sm font-medium text-gray-700">
                Foundation work
              </label>
            </div>

            <div class="flex items-center">
              <input id="landscaping" type="checkbox" v-model="data.amenities_remodeled" value="Landscaping" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="landscaping" class="ml-3 block text-sm font-medium text-gray-700">
                Landscaping
              </label>
            </div>

            <div class="flex items-center">
              <input id="light_fixtures_and_or_faucets" type="checkbox" v-model="data.amenities_remodeled" value="Light fixtures and/or faucets" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="light_fixtures_and_or_faucets" class="ml-3 block text-sm font-medium text-gray-700">
                Light fixtures and/or faucets
              </label>
            </div>

            <div class="flex items-center">
              <input id="appliances" type="checkbox" v-model="data.amenities_remodeled" value="Appliances" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="appliances" class="ml-3 block text-sm font-medium text-gray-700">
                Appliances
              </label>
            </div>

            <div class="flex items-center">
              <input id="installed_a_swimming_pool" type="checkbox" v-model="data.amenities_remodeled" value="Installed a swimming pool" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="installed_a_swimming_pool" class="ml-3 block text-sm font-medium text-gray-700">
                Installed a swimming pool
              </label>
            </div>

            <div class="flex items-center">
              <input id="roof" type="checkbox" v-model="data.amenities_remodeled" value="Roof" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="roof" class="ml-3 block text-sm font-medium text-gray-700">
                Roof
              </label>
            </div>

            <div class="flex items-center">
              <input id="air_conditioning_or_heating_units" type="checkbox" v-model="data.amenities_remodeled" value="Air conditioning or heating units" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="air_conditioning_or_heating_units" class="ml-3 block text-sm font-medium text-gray-700">
                Air conditioning or heating units
              </label>
            </div>

            <div class="flex items-center">
              <input id="windows" type="checkbox" v-model="data.amenities_remodeled" value="Windows" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="windows" class="ml-3 block text-sm font-medium text-gray-700">
                Windows
              </label>
            </div>

            <div class="flex items-center">
              <input id="hot_water_heater" type="checkbox" v-model="data.amenities_remodeled" value="Hot water heater" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="hot_water_heater" class="ml-3 block text-sm font-medium text-gray-700">
                Hot water heater
              </label>
            </div>

            <div class="flex items-center">
              <input id="significant_wall_repair" type="checkbox" v-model="data.amenities_remodeled" value="Significant wall repair" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="significant_wall_repair" class="ml-3 block text-sm font-medium text-gray-700">
                Significant wall repair
              </label>
            </div>

            <div class="flex items-center">
              <input id="complete_kitchen_remodel" type="checkbox" v-model="data.amenities_remodeled" value="Complete kitchen remodel" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="complete_kitchen_remodel" class="ml-3 block text-sm font-medium text-gray-700">
                Complete kitchen remodel
              </label>
            </div>

            <div class="flex items-center">
              <input id="complete_bathroom_remodel" type="checkbox" v-model="data.amenities_remodeled" value="Complete bathroom remodel" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="complete_bathroom_remodel" class="ml-3 block text-sm font-medium text-gray-700">
                Complete bathroom remodel
              </label>
            </div>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.amenities_remodeled">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.amenities_remodeled }}
        </div>
      </fieldset>

      <div class="mt-6 mb-3">
        <div class="rounded-md bg-blue-50 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3 flex-1 md:flex md:justify-between">
              <p class="text-sm text-blue-700">We want to help you save money on housing expenses any way we can. These are companies we have partnered with to provide you with additional savings. When you sign up and use these services, they contribute to us to help fund our ability to help more homeowners in need.</p>
            </div>
          </div>
        </div>
      </div>

      <!--Refinance-->
      <fieldset>
        <div class="mt-6 mb-3">
          <h4 class="font-semibold text-gray-900 mb-2">Refinance</h4>
          <p class="text-sm font-medium block text-gray-600">I am a loan officer on the Alex Varela team at
            Neighborhood Loans. Alex has been my go-to lender since 2004. The only way I would refinance is with
            his
            team’s help.</p>
          <div class="mt-2 grid grid-cols-12">
            <ul class="text-sm font-medium text-gray-500 leading-6 list-disc pl-7 col-span-12 sm:col-span-5">
              <li>
                By refinancing we can help you
                <ul class="list-disc pl-7">
                  <li>Consolidate debt and reduce your total payments</li>
                  <li>Reduce your term to pay off your house quicker</li>
                  <li>Lower your payment</li>
                  <li>Tap into your equity for home improvements or to invest</li>
                </ul>
              </li>
            </ul>

            <div class="col-span-12 sm:col-span-5 mt-2 sm:mt-1">
              <a href="https://youtu.be/5eVu0eoQxiE" target="_blank">
                <img :src="siteUrl + '/assets/images/thumbnail-video-img-2.png'" style="border-radius: 8px; height: 120px;">
              </a>
            </div>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Get a no cost no obligation refinance quote
            <br>
            <span class="text-gray-400">I will email you a link to the application and copy Alex</span>
          </label>

          <div>
            <select v-model="data.get_free_refinance_quote" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.get_free_electricity_bill_analysis }">
              <option value="" selected disabled>Please Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Maybe">Maybe</option>
            </select>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.get_free_refinance_quote">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.get_free_refinance_quote }}
        </div>
      </fieldset>

      <!--Electricity bill analysis-->
      <fieldset>
        <div class="mt-6">
          <label class="block font-medium text-gray-900 mb-2">
            Electricity
          </label>
          <p class="text-sm font-medium block text-gray-600 mb-1">Save money on your electricity bill by using the
            company that saved Chandler $1,250. Energy Ogre cuts through the marketing gimmicks and
            analyzes
            the electricity rates of all the “A” rated electricity companies in Texas to save you the most money
            possible.</p>
          <p class="text-sm font-medium block text-gray-600 mb-1">Would you like to discover how much they can
            save
            you for free?</p>
          <p class="text-sm font-medium text-gray-400 mb-2">I will email you a link to their free savings
            calculator
          </p>

          <div>
            <select v-model="data.get_free_electricity_bill_analysis" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.get_free_electricity_bill_analysis }">
              <option value="" selected disabled>Please Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Tell me more first">Tell me more first</option>
            </select>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.get_free_electricity_bill_analysis">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.get_free_electricity_bill_analysis }}
        </div>
      </fieldset>

      <!--Do you want to invest in real estate?-->
      <fieldset>
        <div class="mt-7">
          <label class="block text-sm font-medium text-gray-900 mb-3">
            Do you want to invest in real estate?
            <br>
            <span class="text-gray-400">Check all that apply</span>
          </label>

          <div class="space-y-4" :class="{ 'border border-red-500 p-1 rounded': errors.invest }">
            <div class="flex items-center">
              <input id="invest_1" type="checkbox" v-model="data.invest" value="Send Emails" @change="uncheckNoThankYouOption($event)" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="invest_1" class="ml-3 block text-sm font-medium text-gray-700">
                Email me when you have a deeply discounted property for sale
              </label>
            </div>

            <div class="flex items-center">
              <input id="invest_2" type="checkbox" v-model="data.invest" value="Interested In Learning" @change="uncheckNoThankYouOption($event)" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="invest_2" class="ml-3 block text-sm font-medium text-gray-700">
                I am interested in learning how to earn passive income by loaning money on real estate
                investment deals.
              </label>
            </div>

            <div class="flex items-center">
              <input id="invest_3" type="checkbox" v-model="data.invest" value="No Thank You" @change="uncheckOtherInvestOptions($event)" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" />
              <label for="invest_3" class="ml-3 block text-sm font-medium text-gray-700">
                No thank you.
              </label>
            </div>
          </div>
        </div>

        <div class="text-red-600 text-sm px-1 py-1" v-if="errors.invest">
          <i class="fa fa-exclamation-triangle"></i> {{ errors.invest }}
        </div>
      </fieldset>

      <div v-if="!user.help_to_fix_tax_system">
        <h4 class="font-medium text-gray-900 mb-3 mt-6">Beyond protesting, I want to change laws and fix our broken
          tax
          system.</h4>

        <!--Help to fix tax system-->
        <fieldset>
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-3">
              Would you like to be notified when Chandler needs help?
            </label>

            <div>
              <select v-model="data.help_to_fix_tax_system" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.help_to_fix_tax_system }">
                <option value="" selected disabled>Please Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div class="text-red-600 text-sm px-1 py-1" v-if="errors.help_to_fix_tax_system">
              <i class="fa fa-exclamation-triangle"></i> {{ errors.help_to_fix_tax_system }}
            </div>

            <div class="mt-3" v-if="data.help_to_fix_tax_system == 'Other'">
              <input type="text" v-model="data.help_to_fix_tax_system_other" placeholder="Please enter other information" maxlength="250" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-sm border-gray-300 rounded-md" :class="{ 'border-red-500': errors.help_to_fix_tax_system_other }" />
            </div>

            <div class="text-red-600 text-sm px-1 py-1" v-if="errors.help_to_fix_tax_system_other">
              <i class="fa fa-exclamation-triangle"></i> {{ errors.help_to_fix_tax_system_other }}
            </div>

          </div>
        </fieldset>
      </div>

      <div class="w-full mt-4 sm:mt-6 text-center border-t pt-4 sm:pt-6">
        <button :disabled="isProcessing" type="button" @click="submitHandler()" class="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          <i v-if="isProcessing" class="fa fa-spinner fa-spin p-1"></i>
          <span>Submit</span>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import ProtestRequestApi from "@/services/api/ProtestRequest.js";
import PropertyDetails from "./partial/PropertyDetails.vue";
import DonationPage from "./partial/Donation.vue";
import { siteUrl } from "../../../../config/base"
import Step1 from "./Step1.vue";

export default {
  props: ["protest-request", "property", "user",'user-property-amenity', 'isRegistrationClosedOrFullyOpened'],

  components: {
    PropertyDetails,
  },
  data() {
    return {
      data: {},
      errors: {},
      isProcessing: false,
      current_year: new Date().getFullYear(),
    };
  },
  created() {
    this.init();

  },
  computed: {
    siteUrl: function () {
      return siteUrl;
    },
  },
  methods: {
    init() {
      var userPropertyAmenity = this.userPropertyAmenity;
      console.log(userPropertyAmenity);
      this.data = {
        ...this.data,
        ...{
          total_bedrooms: userPropertyAmenity?.total_bedrooms ?? "",
          total_bathrooms: userPropertyAmenity?.total_bathrooms ?? "",
          total_garage: userPropertyAmenity?.total_garage ?? "",
          in_ground_swimming_pool: userPropertyAmenity?.in_ground_swimming_pool ?? "",
          on_a_cul_de_sac: userPropertyAmenity?.on_a_cul_de_sac ?? "",
          designated_historical_home:userPropertyAmenity?.designated_historical_home ?? "",
          kitchen_counter: userPropertyAmenity?.kitchen_counter ?? "",
          flooring: userPropertyAmenity?.flooring ? userPropertyAmenity?.flooring.split(",") : [],
          exterior_features: userPropertyAmenity?.exterior_features ? userPropertyAmenity?.exterior_features.split(",") : [],
          features_increasing_property_value: userPropertyAmenity?.features_increasing_property_value ? userPropertyAmenity?.features_increasing_property_value.split(",") : [],
          features_decreasing_property_value: userPropertyAmenity?.features_decreasing_property_value ? userPropertyAmenity?.features_decreasing_property_value.split(",") : [],
          remodeled: userPropertyAmenity?.remodeled ?? "",
          remodeling_year: userPropertyAmenity?.remodeling_year ?? "",
          remodeling_prompted: userPropertyAmenity?.remodeling_prompted ?? "",
          remodeling_prompted_by_event: userPropertyAmenity?.remodeling_prompted_by_event ?? "",
          amenities_remodeled: userPropertyAmenity?.amenities_remodeled ? userPropertyAmenity?.amenities_remodeled.split(",") : [],
          get_free_refinance_quote: this.user.get_free_refinance_quote ?? "",
          get_free_electricity_bill_analysis: this.user.get_free_electricity_bill_analysis?? "",
          agree_to_terms_and_conditions: "",
          invest: this.user.invest ? this.user.invest.split(",") : [],
          help_to_fix_tax_system: this.user.help_to_fix_tax_system?? "",
          help_to_fix_tax_system_other: this.user.help_to_fix_tax_system_other ?? "",
        },
      };
    },
    submitHandler() {
      console.log(this.data);
      // if (this.validateData()) {
      //   flash(
      //     "A required question needs to be answered. Please review your answers below",
      //     "error"
      //   );
      //   return;
      // }
      window.blockUI();
      ProtestRequestApi.storeAdditionalInformation(
        this.protestRequest.uid,
        this.data
      ).then((response) => {
        window.location.href = siteUrl + "/user-properties";
        window.unblockUI();
      });
    },
    uncheckNoThankYouOption(event) {
      if (event.target.checked) {
        this.data.invest = this.data.invest.filter((option) => {
          return option != "No Thank You";
        });
      }
    },
    uncheckOtherInvestOptions(event) {
      if (event.target.checked) {
        this.data.invest = this.data.invest.filter((option) => {
          return option == "No Thank You";
        });
      }
    },
    validateData() {
      this.errors = {};
      let error = false;

      if (this.data.total_bedrooms == "") {
        this.errors.total_bedrooms = "Please select an option";
        error = true;
      }

      if (this.data.total_bathrooms == "") {
        this.errors.total_bathrooms = "Please select an option";
        error = true;
      }

      if (this.data.total_garage == "") {
        this.errors.total_garage = "Please select an option";
        error = true;
      }

      if (this.data.kitchen_counter == "") {
        this.errors.kitchen_counter = "Please select an option";
        error = true;
      }

      if (this.data.flooring.length == 0) {
        this.errors.flooring = "Please select atleast 1 option";
        error = true;
      }

      if (this.data.exterior_features.length == 0) {
        this.errors.exterior_features = "Please select atleast 1 option";
        error = true;
      }

      if (this.data.features_increasing_property_value.length == 0) {
        this.errors.features_increasing_property_value = "Please select atleast 1 option";
        error = true;
      }

      if (this.data.features_decreasing_property_value.length == 0) {
        this.errors.features_decreasing_property_value = "Please select atleast 1 option";
        error = true;
      }


      if (this.data.remodeled == "") {
        this.errors.remodeled = "Please select an option";
        error = true;
      }

      if (this.data.remodeled == "Yes") {
        if (!this.data.remodeling_year) {
          this.errors.remodeling_year = "Please enter remodeling year";
          error = true;
        }

        if (
          this.data.remodeling_year &&
          (this.data.remodeling_year > this.current_year ||
            this.data.remodeling_year < 1900)
        ) {
          this.errors.remodeling_year =
            "Year must be between 1900 and " + this.current_year;
          error = true;
        }

        if (this.data.remodeling_prompted == "") {
          this.errors.remodeling_prompted = "Please select an option";
          error = true;
        }

        if (
          this.data.remodeling_prompted == "Yes" &&
          this.data.remodeling_prompted_by_event == ""
        ) {
          this.errors.remodeling_prompted_by_event = "Please select an option";
          error = true;
        }

        if (this.data.amenities_remodeled.length == 0) {
          this.errors.amenities_remodeled = "Please select atleast 1 option";
          error = true;
        }
      }

      if (this.data.invest.length == 0 && !this.user.invest) {
        this.errors.invest = "Please select atleast 1 option";
        error = true;
      }

      if (
        this.data.get_free_refinance_quote == "" &&
        !this.user.get_free_refinance_quote
      ) {
        this.errors.get_free_refinance_quote = "Please select an option";
        error = true;
      }

      if (
        this.data.get_free_electricity_bill_analysis == "" &&
        !this.user.get_free_electricity_bill_analysis
      ) {
        this.errors.get_free_electricity_bill_analysis =
          "Please select an option";
        error = true;
      }

      if (
        this.data.help_to_fix_tax_system == "" &&
        !this.user.help_to_fix_tax_system
      ) {
        this.errors.help_to_fix_tax_system = "Please select an option.";
        error = true;
      }

      if (
        this.data.help_to_fix_tax_system == "Other" &&
        this.data.help_to_fix_tax_system_other == ""
      ) {
        this.errors.help_to_fix_tax_system_other =
          "Please answer other information.";
        error = true;
      }

      if (this.data.help_to_fix_tax_system_other.length > 250) {
        this.errors.help_to_fix_tax_system_other =
          "Maximum character allowed are 250";
        error = true;
      }
      return error;
    },
  },
};
</script>
<style>
option[value=""][disabled] {
  color: gray;
}
</style>
